import { normalizeProjectName } from '@better-lights/core';
import { syncHooks } from '../sync/hooks.ts';
import type { BlobStore } from './blobStore.ts';
import { db } from './db.ts';
import { normalizeFileUris, type ProjectFile, referencedBlobs } from './record.ts';

/** Project documents in Dexie; blob garbage collection on delete. */
export class ProjectRepo {
  constructor(private readonly blobs: BlobStore) {}

  async list(): Promise<ProjectFile[]> {
    const all = await db().projects.toArray();
    return all
      .filter((p) => !p.deletedAt)
      .map(normalizeFileUris)
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }

  async get(id: string): Promise<ProjectFile | undefined> {
    const f = await db().projects.get(id);
    return f ? normalizeFileUris(f) : undefined;
  }

  async save(file: ProjectFile): Promise<void> {
    await db().projects.put(file);
  }

  async rename(id: string, name: string): Promise<void> {
    const file = await this.get(id);
    if (!file) return;
    const clean = normalizeProjectName(name);
    const next: ProjectFile = { ...file, updatedAt: Date.now() };
    if (clean) next.name = clean;
    else delete next.name;
    await this.save(next);
    syncHooks.projectSaved(id);
  }

  /** Deletes the project, then every blob no other project references. */
  async delete(id: string): Promise<void> {
    const file = await this.get(id);
    await db().projects.delete(id);
    syncHooks.projectDeleted(id);
    if (file) await this.collect(referencedBlobs(file));
  }

  /** Deletes the given blobs unless another project still references them. */
  async collect(candidates: string[]): Promise<void> {
    if (candidates.length === 0) return;
    const live = new Set<string>();
    for (const p of await db().projects.toArray()) for (const sha of referencedBlobs(p)) live.add(sha);
    for (const sha of candidates) if (!live.has(sha)) await this.blobs.delete(sha);
  }
}
